import { useState } from 'react';
import { ETATS_COMMANDE, type EtatCommande } from '@artosera/shared';
import { Button, Price, Select } from '@artosera/ui';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import {
  useCommandesBoutique,
  useReglagesBoutique,
  type CommandePresentee,
} from '../../api/boutique.js';
import { galerieALaction } from '../../auth/sujet.js';
import { DefilementHorizontal } from '../../composants/DefilementHorizontal.js';
import { Monogramme } from '../../composants/Monogramme.js';
import { ObjetCartel } from '../../composants/ObjetCartel.js';
import { useLangue } from '../../composants/useLangue.js';
import { useShellContext } from '../shell/context.js';
import {
  EtatCommandeBadge,
  EtatVide,
  ErreurBoutique,
  PaginationBoutique,
  VignetteBoutique,
  useAllerAOnglet,
} from './commun.js';
import { etapeSuivante, useSyntheseBoutique } from './donneesBoutique.js';
import styles from './BoutiquePage.module.css';
/** Commandes qui attendent la galerie, avec la prochaine étape et le bouton pour la mener. */
const MAX_A_TRAITER = 5;
function ATraiter({ commandes, total }: { commandes: CommandePresentee[]; total: number }) {
  const { t } = useTranslation('regie');
  const { galerieSlug, galerie } = useShellContext();
  const langue = useLangue();
  const navigate = useNavigate();
  const peutRembourser = galerieALaction(galerie, 'orders:refund');
  if (!commandes.length) return null;
  return (
    <section className={styles.aTraiter} id="a-traiter" aria-labelledby="boutique-a-traiter">
      <div>
        <h2 id="boutique-a-traiter">{t('boutique.aTraiter.titre')}</h2>
        <p className={styles.secondaire}>{t('boutique.aTraiter.aide')}</p>
      </div>
      <ul className={styles.fiches}>
        {commandes.slice(0, MAX_A_TRAITER).map((c) => {
          const etape = etapeSuivante(c.etat);
          const aTerminer = c.etat === 'a_rembourser' && peutRembourser;
          return (
            <li key={c.id} className={styles.fiche} data-etat={c.etat}>
              <Link className={styles.cartelLien} to={`/${galerieSlug}/boutique/commandes/${c.id}`}>
                <VignetteBoutique
                  galerie={galerieSlug}
                  mediaId={c.oeuvre.mediaPrincipalId}
                  url={c.oeuvre.apercuUrl}
                />
                <ObjetCartel titre={c.oeuvre.titre} artiste={c.oeuvre.artisteNom} />
              </Link>
              <div className={styles.ficheCorps}>
                <span className={styles.personne}>
                  <Monogramme nom={c.contact.nomAffiche} taille={28} />
                  <span>{c.contact.nomAffiche}</span>
                </span>
                <Price cents={c.montantCentimes} lang={langue} />
                <EtatCommandeBadge etat={c.etat} />
              </div>
              <p className={styles.ficheEtape}>
                <strong>{t('boutique.etapes.titre')}</strong>
                {etape ? t(`boutique.etapes.${etape}`) : t('boutique.etapes.aucune')}
              </p>
              <Button
                variant={aTerminer ? 'primary' : 'secondary'}
                onClick={() => navigate(`/${galerieSlug}/boutique/commandes/${c.id}`)}
              >
                {t(aTerminer ? 'boutique.terminerRemboursement' : 'boutique.aTraiter.ouvrir')}
              </Button>
            </li>
          );
        })}
      </ul>
      {total > MAX_A_TRAITER && (
        <p className={styles.secondaire}>
          {t('boutique.aTraiter.autres', { count: total - MAX_A_TRAITER })}
        </p>
      )}
    </section>
  );
}
function CommandesVides({ filtre, reinitialiser }: { filtre: boolean; reinitialiser: () => void }) {
  const { t } = useTranslation('regie');
  const { galerieSlug } = useShellContext();
  const allerAOnglet = useAllerAOnglet();
  const reglages = useReglagesBoutique(galerieSlug);
  const synthese = useSyntheseBoutique(galerieSlug);
  if (filtre)
    return (
      <EtatVide
        titre={t('boutique.videFiltre.titre')}
        aide={t('boutique.videFiltre.aide')}
        action={{ libelle: t('boutique.videFiltre.action'), onClick: reinitialiser }}
      />
    );
  if (reglages.data && !reglages.data.ouverte)
    return (
      <EtatVide
        titre={t('boutique.videFermee.titre')}
        aide={t('boutique.videFermee.aide')}
        action={{
          libelle: t('boutique.videFermee.action'),
          onClick: () => allerAOnglet('conditions'),
        }}
      />
    );
  if (synthese.nbOeuvresEnVente === 0)
    return (
      <EtatVide
        titre={t('boutique.videSansOeuvre.titre')}
        aide={t('boutique.videSansOeuvre.aide')}
        action={{
          libelle: t('boutique.videSansOeuvre.action'),
          onClick: () => allerAOnglet('oeuvres'),
        }}
      />
    );
  return <EtatVide titre={t('boutique.videAttente.titre')} aide={t('boutique.videAttente.aide')} />;
}
export function CommandesTab() {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug } = useShellContext();
  const langue = useLangue();
  const synthese = useSyntheseBoutique(galerieSlug);
  const [etat, setEtat] = useState<EtatCommande | ''>('');
  const [page, setPage] = useState(1);
  const liste = useCommandesBoutique(galerieSlug, { ...(etat ? { etat } : {}), page });
  return (
    <div className={styles.contenu}>
      <ATraiter commandes={synthese.aTraiter} total={synthese.nbATraiter ?? 0} />
      <div className={styles.filtres}>
        <label className={styles.filtre}>
          <span>{t('regie:boutique.etat')}</span>
          <Select
            aria-label={t('regie:boutique.etat')}
            value={etat}
            placeholder={t('regie:boutique.tousEtats')}
            onValueChange={(v) => {
              setEtat(v as EtatCommande | '');
              setPage(1);
            }}
            options={[
              { value: '', label: t('regie:boutique.tousEtats') },
              ...ETATS_COMMANDE.map((e) => ({ value: e, label: t(`regie:boutique.etats.${e}`) })),
            ]}
          />
        </label>
        <span className={styles.secondaire}>
          {liste.data
            ? t('regie:boutique.totalCommandes', { count: liste.data.pagination.total })
            : ''}
        </span>
      </div>
      {liste.isLoading ? (
        <p role="status">{t('commun:chargement')}</p>
      ) : liste.isError ? (
        <ErreurBoutique erreur={liste.error} reessayer={() => void liste.refetch()} />
      ) : !liste.data?.commandes.length ? (
        <CommandesVides
          filtre={!!etat}
          reinitialiser={() => {
            setEtat('');
            setPage(1);
          }}
        />
      ) : (
        <>
          <DefilementHorizontal>
            <table className={styles.tableau} data-density="registre">
              <thead>
                <tr>
                  {['oeuvre', 'acheteur', 'montant', 'etat', 'prochaineEtape', 'facture'].map(
                    (k) => (
                      <th scope="col" key={k}>
                        {t(`regie:boutique.${k}`)}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {liste.data.commandes.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <Link
                        className={styles.cartelLien}
                        to={`/${galerieSlug}/boutique/commandes/${c.id}`}
                      >
                        <VignetteBoutique
                          galerie={galerieSlug}
                          mediaId={c.oeuvre.mediaPrincipalId}
                          url={c.oeuvre.apercuUrl}
                        />
                        <ObjetCartel titre={c.oeuvre.titre} artiste={c.oeuvre.artisteNom} />
                      </Link>
                    </td>
                    <td data-libelle={t('regie:boutique.acheteur')}>
                      <span className={styles.personne}>
                        <Monogramme nom={c.contact.nomAffiche} taille={28} />
                        <span>{c.contact.nomAffiche}</span>
                      </span>
                    </td>
                    <td data-libelle={t('regie:boutique.montant')}>
                      <Price cents={c.montantCentimes} lang={langue} />
                    </td>
                    <td data-libelle={t('regie:boutique.etat')}>
                      <EtatCommandeBadge etat={c.etat} />
                    </td>
                    <td
                      className={styles.celEtape}
                      data-libelle={t('regie:boutique.prochaineEtape')}
                    >
                      <span className={styles.secondaire}>
                        {etapeSuivante(c.etat)
                          ? t(`regie:boutique.etapes.${etapeSuivante(c.etat)}`)
                          : '—'}
                      </span>
                    </td>
                    <td data-libelle={t('regie:boutique.facture')}>
                      {c.facture ? (
                        <Link className={styles.lien} to={`/${galerieSlug}/ventes/${c.facture.id}`}>
                          {c.facture.numero}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DefilementHorizontal>
          <PaginationBoutique pagination={liste.data.pagination} changer={setPage} />
        </>
      )}
    </div>
  );
}
